import { io, ManagerOptions, Socket, SocketOptions } from "socket.io-client";
import { PageSlaveMode } from "../../components/navigation/PageContext";
import { SessionMode } from "../../store/session/slices";
import { logger } from "../logger";
import { toastError } from "../toast";
import { MessagingBridgeBroadcastChannelMessage, MessagingBridgeClientData, MessagingBridgeClientDataMap, MessagingBridgeManager, MessagingBridgeManagerOptions, MessagingBridgeMessage, MessagingBridgeMessageAction, MessagingBridgeMessageExtended } from "./common";

const SLAVE_PING_TIMEOUT = 10000;
const SLAVE_WEBSOCKET_RECONNECTION_DELAY = 500;

export interface MessagingBridgeManagerSlaveOptions extends MessagingBridgeManagerOptions {
  websocketEndpoint?: string;
  enableWebsocket?: boolean;
}

const defaultBridgeSlaveMessageManagerOptions = {
  enableWebsocket: false,
  websocketEndpoint: undefined,
} as const;

export interface MessagingBridgeManagerSlaveEventMap {
  ready: () => void;
  unauthorized: (msg: MessagingBridgeMessageExtended<MessagingBridgeMessageAction.UNAUTHORIZED>) => void;
  meeting: (msg: MessagingBridgeMessageExtended<MessagingBridgeMessageAction.SET_MEETING>) => void;
  chat: (msg: MessagingBridgeMessageExtended<MessagingBridgeMessageAction.SET_CHAT>) => void;
  qrCodeData: (msg: MessagingBridgeMessageExtended<MessagingBridgeMessageAction.SET_QR_CODE_DATA>) => void;
  displayCarousel: (msg: MessagingBridgeMessageExtended<MessagingBridgeMessageAction.DISPLAY_CAROUSEL>) => void;
  currentExpertView: (msg: MessagingBridgeMessageExtended<MessagingBridgeMessageAction.SET_CURRENT_EXPERT_VIEW>) => void;
  ping: (msg: MessagingBridgeMessageExtended<MessagingBridgeMessageAction.PING>) => void;
  disconnect: () => void;
}

export class MessagingBridgeManagerSlave extends MessagingBridgeManager<
  SessionMode.SLAVE,
  keyof MessagingBridgeManagerSlaveEventMap,
  MessagingBridgeManagerSlaveEventMap
> {
  readonly websocketEnabled: boolean;
  readonly websocketEnpoint?: string;

  protected masterId?: string;
  private socket?: Socket;

  private pingTimeout?: NodeJS.Timer;
  private reconnectTimeout?: NodeJS.Timeout;

  constructor(options?: MessagingBridgeManagerSlaveOptions) {
    super(SessionMode.SLAVE, options);
    this.websocketEnabled = options?.enableWebsocket ?? defaultBridgeSlaveMessageManagerOptions.enableWebsocket;
    this.websocketEnpoint = options?.websocketEndpoint ?? defaultBridgeSlaveMessageManagerOptions.websocketEndpoint;
    if (this.websocketEnabled) {
      this.connectWebsocket();
    }

    this.appListener = [
      [
        "applicationMessage",
        (id: string, msg: MessagingBridgeMessageExtended<any>) => {
          // if (id !== msg.fromId) return;
          this.proccess(msg);
        },
      ],
    ];
    this.appListener.forEach((listener) => this.appMessage?.addEventListener(...listener));

    this.appBroadcastChannel?.addEventListener("applicationMessage", (id: string, msg: MessagingBridgeBroadcastChannelMessage<any>) => {
      if (id !== msg.fromId) return;
      this.proccess(msg, msg.toId);
    });
    this.appBroadcastChannel?.addEventListener("applicationRequestJoin", (toId: string) => {
      if (toId === this.id) {
        this.appBroadcastChannel?.joinApplication(this.id);
      }
    });
    this.appBroadcastChannel?.joinApplication(this.id);
  }

  private connectWebsocket() : boolean{
    if (this.reconnectTimeout) {
      clearTimeout(this.reconnectTimeout);
      this.reconnectTimeout = undefined;
    }
    if (this.socket) {
      this.socket.off();
      this.socket.disconnect();
      this.socket = undefined;
    }
    if (!this.websocketEnpoint) throw new Error('"websocketEnpoint" need to be defined if the websocket is enabled');
    const options: Partial<ManagerOptions & SocketOptions> = {
      forceNew: true,
      transports: ["websocket"],
      reconnectionDelay: SLAVE_WEBSOCKET_RECONNECTION_DELAY,
      autoConnect: true,
    };
    let endpoint: URL | undefined;
    try {
      endpoint = new URL(this.websocketEnpoint);
    } catch(err) {
      toastError(`Remote URL is NOT valid : ${this.websocketEnpoint}`);
    }
    if(!endpoint) return false;
    const password = endpoint.searchParams.get("password") ?? this.password;
    if (password) endpoint.searchParams.set("password", password);
    const socket = io(endpoint.href, options);
    this.socket = socket;
    socket.on("message", (...args: any[]) => {
      if (!args?.length) return;
      const msg = args[0];
      this.proccess(msg);
    });
    socket.on("error", (...args: any[]) => {
      logger.log("Error : ", ...args);
    });
    socket.on("disconnect", (...args: any[]) => {
      logger.log("Disconnect : ", ...args);
      if (this.reconnectTimeout) clearTimeout(this.reconnectTimeout);
      this.reconnectTimeout = undefined;
      this.reconnectTimeout = setTimeout(() => {
        this.reconnectTimeout = undefined;
        this.connectWebsocket();
      }, SLAVE_WEBSOCKET_RECONNECTION_DELAY);
    });
    return true;
  }

  private isMasterId(id: string): boolean {
    return this.masterId === id;
  }

  private clearTimeout() {
    if (this.pingTimeout) clearTimeout(this.pingTimeout);
    this.pingTimeout = undefined;
  }

  private restartPingTimeout() {
    this.clearTimeout();
    this.pingTimeout = setTimeout(() => {
      // connection lost
      this.events.triggerEvent("disconnect");
    }, SLAVE_PING_TIMEOUT);
  }

  post<T extends MessagingBridgeMessageAction>(msg: MessagingBridgeMessage<T>, toId: string | undefined = this.masterId): MessagingBridgeMessage<T> | undefined {
    this.log("POST message : ", msg, " toId : ", toId);

    const extendedMsg: MessagingBridgeMessageExtended<T> = {
      ...msg,
      fromId: this.id,
      from: this.mode,
    };
    if (this.appBroadcastChannel) {
      const broadcastMessage: MessagingBridgeBroadcastChannelMessage<T> = {
        ...extendedMsg,
        toId,
      };
      this.appBroadcastChannel.postApplicationMessage(this.id, broadcastMessage);
    }
    this.appMessage?.postApplicationMessage(extendedMsg, toId);
    if (this.websocketEnabled && this.socket?.id) {
      extendedMsg.fromId = this.socket.id;
      this.socket.emit("message", extendedMsg);
    }
    return msg;
  }

  postToMaster<T extends MessagingBridgeMessageAction>(msg: MessagingBridgeMessage<T>) {
    if (!this.masterId) return false;
    return this.post(msg, this.masterId);
  }

  protected proccess(msg: MessagingBridgeMessageExtended<any>, toId?: string): void {
    this.log("RECEIVE message : ", msg, " toId : ", toId);
    if (toId && toId !== this.id) return;
    if (MessagingBridgeManagerSlave.isMessageExtented(msg, MessagingBridgeMessageAction.UNAUTHORIZED)) {
      this.events.triggerEvent("unauthorized", msg);
      return;
    }
    if (MessagingBridgeManagerSlave.isMessageExtented(msg, MessagingBridgeMessageAction.WELCOME)) {
      this.masterId = msg.content.masterId;
      this.events.triggerEvent("ready");
      return;
    }
    if (MessagingBridgeManagerSlave.isMessageExtented(msg, MessagingBridgeMessageAction.PING)) {
      this.restartPingTimeout();
      this.events.triggerEvent("ping", msg);
      this.postPong(msg.content.counter, msg.fromId);
      return;
    }

    if (MessagingBridgeManagerSlave.isMessageExtented(msg, MessagingBridgeMessageAction.SET_MEETING)) {
      this.events.triggerEvent("meeting", msg);
      return;
    }

    if (MessagingBridgeManagerSlave.isMessageExtented(msg, MessagingBridgeMessageAction.SET_CHAT)) {
      this.events.triggerEvent("chat", msg);
      return;
    }

    if (MessagingBridgeManagerSlave.isMessageExtented(msg, MessagingBridgeMessageAction.DISPLAY_CAROUSEL)) {
      this.events.triggerEvent("displayCarousel", msg);
      return;
    }

    if (MessagingBridgeManagerSlave.isMessageExtented(msg, MessagingBridgeMessageAction.SET_CURRENT_EXPERT_VIEW)) {
      this.events.triggerEvent("currentExpertView", msg);
      return;
    }

    if (MessagingBridgeManagerSlave.isMessageExtented(msg, MessagingBridgeMessageAction.SET_QR_CODE_DATA)) {
      this.events.triggerEvent("qrCodeData", msg);
      return;
    }
  }

  public async destroy() {
    if (this.appBroadcastChannel) await this.appBroadcastChannel.leaveApplication(this.id);
    if (this.socket) {
      this.socket.off();
      this.socket.disconnect();
    }
    super.destroy();
  }

  // --------------------------- POST --------------------- //

  postPong(counter: number, toId: string | undefined = this.masterId) {
    const msg = this.buildBridgeMessage(MessagingBridgeMessageAction.PONG, {
      counter,
    });
    return this.post(msg, toId);
  }

  postClientData<T extends PageSlaveMode>(page: T, data?: T extends keyof MessagingBridgeClientDataMap[SessionMode.SLAVE] ? MessagingBridgeClientDataMap[SessionMode.SLAVE][T] : undefined, toId: string | undefined = this.masterId) {
    const msg = this.buildBridgeMessage(MessagingBridgeMessageAction.SET_CLIENT_DATA, {
      mode: SessionMode.SLAVE,
      page,
      data,
    } as MessagingBridgeClientData<SessionMode.SLAVE, T>);
    return this.post(msg, toId);
  }

  postClearClientData(page: PageSlaveMode, toId: string | undefined = this.masterId) {
    const msg = this.buildBridgeMessage(MessagingBridgeMessageAction.CLEAR_CLIENT_DATA, {
      mode: SessionMode.SLAVE,
      page,
    } as any);
    return this.post(msg, toId);
  }

  postRequestMeeting(toId: string | undefined = this.masterId) {
    const msg = this.buildBridgeMessage(MessagingBridgeMessageAction.GET_MEETING, undefined);
    return this.post(msg, toId);
  }

  postRequestChat(chatroomId: string, toId: string | undefined = this.masterId) {
    const msg = this.buildBridgeMessage(MessagingBridgeMessageAction.GET_CHAT, { chatroomId });
    return this.post(msg, toId);
  }
}
